<script setup lang="ts">
    // @ts-expect-error - no types for autosize
    import autosize from 'autosize'
    import {
        computed,
        nextTick,
        onBeforeUnmount,
        onMounted,
        ref,
        useId,
        useTemplateRef,
        watch,
    } from 'vue'
    import { useResizeObserver } from '@vueuse/core'
    import { codeExtensionsOf, type AgentFileUpload } from 'models'
    import type { PendingAttachment } from 'composables'
    import PkStreamingMarkdown from './PkStreamingMarkdown.vue'
    import PkChatbotFilePreview from './PkChatbotFilePreview.vue'
    import PkChatButton from './PkChatButton.vue'

    // --- Models & Props ---

    const modelValue = defineModel<string | undefined>({ default: undefined })
    const pendingAttachments = defineModel<PendingAttachment[]>(
        'pendingAttachments',
        { default: () => [] },
    )

    const emit = defineEmits<{
        submit: []
        'stop-generation': []
        'file-select': [file: File]
        'dismiss-attachment-notice': []
        'dismiss-page-notice': []
        'open-answer-notice': [id: string]
        'dismiss-answer-notice': [id: string]
        'voice-start': []
        /** Point at the page (spec `page-point-and-choose.md`, P). */
        point: []
        'remove-pointer': []
    }>()

    const props = defineProps<{
        placeholder?: string
        dismissableNotice?: string
        /**
         * What the agent can do on the page that embeds the chat (spec
         * `page-tools.md`). Its closing is the caller's to remember.
         */
        pageNotice?: string
        status?: 'submitted' | 'streaming' | 'ready' | 'error'
        maxMessageLength?: number
        fileUpload?: AgentFileUpload
        attachmentLimitReached?: boolean
        attachmentNotice?: string
        /** Show the talk button: the agent has voice enabled. */
        voiceEnabled?: boolean
        /**
         * What is written while the agent answers is queued and sent when the
         * answer ends (spec resumable-chat-streams, F2): sending stays open.
         */
        queueEnabled?: boolean
        /** Answers that ended in another conversation, each with its own line and its "Open". */
        answerNotices?: { id: string; text: string }[]
        /** The user may point at the page that embeds the chat: the button shows. */
        pointerAvailable?: boolean
        /** The user is pointing at the page now. */
        pointing?: boolean
        /** What the user pointed at for this message: the chip above the field. */
        pointer?: { kind: 'element' | 'text'; label: string }
    }>()

    // --- Template refs ---

    const textareaEl = useTemplateRef<HTMLTextAreaElement>('textareaEl')
    const fileInputEl = useTemplateRef<HTMLInputElement>('fileInputEl')
    const cameraInputEl = useTemplateRef<HTMLInputElement>('cameraInputEl')
    const formEl = useTemplateRef<HTMLFormElement>('formEl')

    // --- Input buffering (decouples typing from reactive model propagation) ---

    const localValue = ref(modelValue.value ?? '')
    let syncTimer: ReturnType<typeof setTimeout> | null = null

    const cancelSync = () => {
        if (syncTimer !== null) {
            clearTimeout(syncTimer)
            syncTimer = null
        }
    }

    const flushSync = () => {
        cancelSync()
        modelValue.value = localValue.value
    }

    const scheduleSync = () => {
        cancelSync()
        syncTimer = setTimeout(() => {
            modelValue.value = localValue.value
            syncTimer = null
        }, 300)
    }

    const onTextareaInput = () => {
        localValue.value = textareaEl.value?.value ?? ''
        scheduleSync()
    }

    watch(modelValue, (v) => {
        if (v !== localValue.value) {
            localValue.value = v ?? ''
            nextTick(() => {
                if (textareaEl.value) {
                    autosize.update(textareaEl.value)
                }
            })
        }
    })

    // --- Status computeds ---

    const isStreaming = computed(() => props.status === 'streaming')
    const isSubmitted = computed(() => props.status === 'submitted')
    const isInputDisabled = computed(
        () => isStreaming.value || isSubmitted.value,
    )

    // --- Validation ---

    const counterId = useId()
    const currentLength = computed(() => localValue.value.length)

    const isOverLimit = computed(
        () =>
            props.maxMessageLength !== undefined &&
            currentLength.value > props.maxMessageLength,
    )

    const isUploadingAnyFile = computed(() =>
        pendingAttachments.value.some((a) => a.state === 'uploading'),
    )

    const isSubmitEnabled = computed(() => {
        if (
            (isInputDisabled.value && !props.queueEnabled) ||
            isUploadingAnyFile.value ||
            isOverLimit.value
        ) {
            return false
        }
        return localValue.value.trim().length > 0
    })

    /** Written during an answer: the send button queues it, next to the stop. */
    const isQueueing = computed(
        () => isInputDisabled.value && isSubmitEnabled.value,
    )

    // --- File upload ---

    const hasCaptureSupport = 'capture' in document.createElement('input')

    // A browser filters the picker by type and knows few code types: their
    // extensions go along.
    const acceptMimeTypes = computed(() => {
        const allowed = props.fileUpload?.allowedMimeTypes ?? []
        return [...allowed, ...codeExtensionsOf(allowed)].join(',')
    })

    const acceptImageMimeTypes = computed(() =>
        (props.fileUpload?.allowedMimeTypes ?? [])
            .filter((m) => m.startsWith('image/'))
            .join(','),
    )

    const showCameraOption = computed(
        () => hasCaptureSupport && acceptImageMimeTypes.value.length > 0,
    )

    const openFilePicker = () => {
        fileInputEl.value?.click()
    }

    const openCameraPicker = () => {
        cameraInputEl.value?.click()
    }

    // The store owns the attachment limits and drops any overflow; the input
    // just forwards every picked/dropped file.
    const emitFiles = (files: File[]) => {
        for (const file of files) {
            emit('file-select', file)
        }
    }

    const onFileChange = (event: Event) => {
        const input = event.target as HTMLInputElement
        emitFiles(Array.from(input.files ?? []))
        input.value = ''
    }

    // --- Attachments ---

    const removeAttachment = (id: string) => {
        const att = pendingAttachments.value.find((a) => a.id === id)
        if (att) {
            URL.revokeObjectURL(att.localUrl)
        }
        pendingAttachments.value = pendingAttachments.value.filter(
            (a) => a.id !== id,
        )
    }

    // --- Notices (one box above the input) ---

    type InputNotice = {
        id: string
        text?: string
        markdown?: string
        icon?: string
        open?: () => void
        dismiss?: () => void
    }

    const isDismissableNoticeVisible = ref(true)
    const closeDismissableNotice = () => {
        isDismissableNoticeVisible.value = false
    }

    const activeNotices = computed<InputNotice[]>(() => {
        // The alert answers what the user just did, and takes the box alone
        // until it is closed.
        if (props.attachmentNotice) {
            return [
                {
                    id: 'limit',
                    icon: 'ri:information-line',
                    text: props.attachmentNotice,
                    dismiss: () => emit('dismiss-attachment-notice'),
                },
            ]
        }
        const notices: InputNotice[] = []
        // An answer that ended where the user is not: news, so it comes
        // first, and it leaves once opened or closed.
        for (const notice of props.answerNotices ?? []) {
            notices.push({
                id: `answer-${notice.id}`,
                icon: 'ri:chat-check-line',
                text: notice.text,
                open: () => emit('open-answer-notice', notice.id),
                dismiss: () => emit('dismiss-answer-notice', notice.id),
            })
        }
        // What the agent does on this page, next: the user should read it
        // before writing. The agent's own notice stays beside it, never
        // behind it: it is where an agent states its terms, a privacy
        // policy among them.
        if (props.pageNotice) {
            notices.push({
                id: 'page',
                icon: 'ri:pages-line',
                text: props.pageNotice,
                dismiss: () => emit('dismiss-page-notice'),
            })
        }
        if (props.dismissableNotice && isDismissableNoticeVisible.value) {
            notices.push({
                id: 'dismissable',
                markdown: props.dismissableNotice,
                dismiss: closeDismissableNotice,
            })
        }
        return notices
    })

    // --- Submit ---

    /**
     * Send turns disabled and then gives way to stop, stop gives way to send:
     * a keyboard user who pressed one would be left on the page body. Hand
     * the focus to the textarea, but only after a keyboard press: a tap has no
     * focus to keep, and focusing would bring the phone keyboard back up over
     * the answer.
     */
    const keepKeyboardFocus = () => {
        if (formEl.value?.querySelector('button:focus-visible')) {
            textareaEl.value?.focus()
        }
    }

    const stopGeneration = () => {
        keepKeyboardFocus()
        emit('stop-generation')
    }

    const handleSubmit = () => {
        if (!isSubmitEnabled.value) {
            return
        }
        keepKeyboardFocus()
        flushSync()
        emit('submit')
        localValue.value = ''
        modelValue.value = ''
        nextTick(() => {
            if (textareaEl.value) {
                textareaEl.value.value = ''
                autosize.update(textareaEl.value)
            }
        })
    }

    /**
     * Enter sends, Shift+Enter goes to a new line. The Enter that confirms an
     * IME composition (Japanese, Chinese, Korean) belongs to the composition:
     * Safari reports it with `isComposing` already false, hence the 229.
     */
    const onTextareaEnter = (event: KeyboardEvent) => {
        if (event.shiftKey || event.isComposing || event.keyCode === 229) {
            return
        }
        event.preventDefault()
        handleSubmit()
    }

    // --- Lifecycle ---

    onMounted(() => {
        if (textareaEl.value) {
            autosize(textareaEl.value)
        }
    })

    // autosize measures on input and on a window resize only, and an empty
    // textarea measures its placeholder. The chat settles its layout after
    // mount: measured at a first, narrower width, the placeholder wrapped,
    // and on the chat page of a phone the field stayed 88px tall, four lines
    // with the text at their top, until the user typed. Measured again when
    // its width changes, or its placeholder.
    let textareaWidth = 0
    useResizeObserver(textareaEl, ([entry]) => {
        const width = entry?.contentRect.width ?? 0
        if (textareaEl.value && width !== textareaWidth) {
            textareaWidth = width
            autosize.update(textareaEl.value)
        }
    })
    watch(
        () => props.placeholder,
        async () => {
            await nextTick()
            if (textareaEl.value) {
                autosize.update(textareaEl.value)
            }
        },
    )

    onBeforeUnmount(() => {
        cancelSync()
        if (textareaEl.value) {
            autosize.destroy(textareaEl.value)
        }
        for (const att of pendingAttachments.value) {
            URL.revokeObjectURL(att.localUrl)
        }
    })

    defineExpose({
        /** Move the focus to the message field */
        focus: () => textareaEl.value?.focus(),
    })
</script>

<template>
    <div class="pk-chatbot-input">
        <div
            v-if="activeNotices.length"
            class="pk-chatbot-input__notice"
            role="status">
            <div
                v-for="notice in activeNotices"
                :key="notice.id"
                class="pk-chatbot-input__notice-item">
                <VvIcon
                    v-if="notice.icon"
                    :name="notice.icon"
                    class="pk-chatbot-input__notice-icon" />
                <PkStreamingMarkdown
                    v-if="notice.markdown"
                    :markdown="notice.markdown"
                    class="wysiwyg pk-chatbot-input__notice-text" />
                <span v-else class="pk-chatbot-input__notice-text">
                    {{ notice.text }}
                </span>
                <VvButton
                    v-if="notice.open"
                    :label="$t('action.open')"
                    modifiers="link"
                    class="pk-chatbot-input__notice-action"
                    @click="notice.open()" />
                <VvButton
                    v-if="notice.dismiss"
                    :title="$t('action.close')"
                    modifiers="action-quiet"
                    icon="ri:close-line"
                    class="w-18 h-18"
                    @click="notice.dismiss()" />
            </div>
        </div>
        <form
            ref="formEl"
            class="pk-chatbot-input__form"
            @submit.prevent="handleSubmit">
            <div v-if="pointer" class="pk-chatbot-input__pointer">
                <VvIcon
                    :name="
                        pointer.kind === 'text'
                            ? 'ri:double-quotes-l'
                            : 'ri:focus-3-line'
                    "
                    class="pk-chatbot-input__pointer-icon" />
                <span class="pk-chatbot-input__pointer-label">
                    {{
                        pointer.kind === 'text'
                            ? `«${pointer.label}»`
                            : pointer.label
                    }}
                </span>
                <VvButton
                    :title="$t('pagePointer.remove')"
                    modifiers="action-quiet"
                    icon="ri:close-line"
                    class="w-18 h-18"
                    @click="emit('remove-pointer')" />
            </div>
            <div
                v-if="pendingAttachments.length"
                class="pk-chatbot-input__attachments">
                <PkChatbotFilePreview
                    v-for="att in pendingAttachments"
                    :key="att.id"
                    :media-type="att.mediaSource?.mimeType ?? att.file.type"
                    :url="att.localUrl"
                    :filename="att.file.name"
                    :size="att.file.size"
                    :loading="att.state === 'uploading'"
                    :error="att.state === 'error' ? att.error : undefined"
                    :removable="att.state !== 'uploading'"
                    compact
                    @remove="removeAttachment(att.id)" />
            </div>
            <div class="pk-chatbot-input__fieldset">
                <slot name="prepend" />
                <VvDropdown
                    v-if="
                        fileUpload &&
                        fileUpload.enabled &&
                        (showCameraOption || $slots['additional-actions'])
                    "
                    v-bind="{
                        placement: 'top-start',
                        modifiers: 'menu',
                        flip: true,
                        offset: 3,
                    }">
                    <template #default="{ init, toggle, aria }">
                        <!-- The only one still written by hand: the dropdown
                         anchors itself to whatever `init` receives, and that
                         has to be the element, not a component instance -->
                        <button
                            :ref="(e: any) => init(e)"
                            v-bind="aria"
                            class="pk-chat-button"
                            type="button"
                            :title="$t('action.attachFile')"
                            :disabled="isInputDisabled"
                            @click="toggle">
                            <VvIcon
                                name="ri:add-line"
                                class="pk-chat-button__icon" />
                        </button>
                    </template>
                    <template #items>
                        <VvDropdownAction
                            v-if="showCameraOption"
                            :disabled="
                                isInputDisabled || attachmentLimitReached
                            "
                            @click="openCameraPicker">
                            <VvIcon
                                name="ri:camera-line"
                                class="pk-chatbot-input__dropdown-icon" />
                            {{ $t('action.takePhoto') }}
                        </VvDropdownAction>
                        <VvDropdownAction
                            :disabled="
                                isInputDisabled || attachmentLimitReached
                            "
                            @click="openFilePicker">
                            <VvIcon
                                name="ri:attachment-line"
                                class="pk-chatbot-input__dropdown-icon" />
                            {{ $t('action.attachFile') }}
                        </VvDropdownAction>
                        <slot name="additional-actions" />
                    </template>
                </VvDropdown>
                <PkChatButton
                    v-else-if="fileUpload && fileUpload.enabled"
                    icon="ri:add-line"
                    :title="$t('action.attachFile')"
                    :disabled="isInputDisabled || attachmentLimitReached"
                    @click="openFilePicker" />
                <input
                    v-if="fileUpload && fileUpload.enabled"
                    ref="fileInputEl"
                    type="file"
                    hidden
                    multiple
                    :accept="acceptMimeTypes"
                    @change="onFileChange" />
                <input
                    v-if="fileUpload && fileUpload.enabled && showCameraOption"
                    ref="cameraInputEl"
                    type="file"
                    capture="environment"
                    hidden
                    :accept="acceptImageMimeTypes"
                    @change="onFileChange" />
                <PkChatButton
                    v-if="pointerAvailable"
                    icon="ri:focus-3-line"
                    :title="$t('pagePointer.start')"
                    :aria-pressed="pointing"
                    :disabled="isInputDisabled"
                    @click="emit('point')" />
                <div class="pk-chatbot-input__content">
                    <textarea
                        ref="textareaEl"
                        :value="localValue"
                        class="pk-chatbot-input__textarea"
                        name="message"
                        rows="1"
                        enterkeyhint="send"
                        :aria-label="$t('label.message')"
                        :aria-describedby="
                            maxMessageLength ? counterId : undefined
                        "
                        :aria-invalid="isOverLimit || undefined"
                        :placeholder
                        @input="onTextareaInput"
                        @keydown.enter="onTextareaEnter"></textarea>
                </div>
                <div
                    v-if="maxMessageLength"
                    :id="counterId"
                    class="pk-chatbot-input__counter"
                    :class="{ 'pk-chatbot-input__counter--over': isOverLimit }">
                    {{ $n(currentLength, 'integer') }}/<span
                        class="opacity-60"
                        >{{ $n(maxMessageLength, 'integer') }}</span
                    >
                </div>
                <PkChatButton
                    v-if="voiceEnabled && !isStreaming"
                    icon="ri:mic-line"
                    :title="$t('action.talk')"
                    :disabled="isInputDisabled"
                    @click="emit('voice-start')" />
                <!-- With a message to queue the send button is the filled one,
                 and the stop steps back beside it -->
                <PkChatButton
                    v-if="isStreaming"
                    icon="ri:stop-fill"
                    :modifiers="isQueueing ? undefined : 'primary'"
                    :title="$t('action.stop')"
                    @click="stopGeneration" />
                <PkChatButton
                    v-if="isQueueing"
                    type="submit"
                    modifiers="primary"
                    icon="ri:arrow-up-line"
                    :title="$t('action.queueMessage')" />
                <PkChatButton
                    v-else-if="!isStreaming"
                    type="submit"
                    modifiers="primary"
                    :icon="isSubmitted ? 'loading-loop' : 'ri:arrow-up-line'"
                    :title="$t('action.submit')"
                    :disabled="!isSubmitEnabled" />
            </div>
        </form>
    </div>
</template>

<style lang="scss">
    .pk-chatbot-input {
        position: relative;
        display: flex;
        flex-direction: column;
        margin-inline: var(--spacing-sm);
        margin-bottom: var(--spacing-sm);

        &__notice {
            position: relative;
            padding: var(--spacing-10) var(--spacing-sm);
            display: flex;
            flex-direction: column;
            gap: var(--spacing-10);
            background-color: var(--color-surface-1);
            border-top-left-radius: var(--rounded-xl);
            border-top-right-radius: var(--rounded-xl);
            transition-property: var(--transition-property-all);
            transition-duration: var(--duration-300);
            transition-timing-function: var(--ease-in-out);
            font-size: var(--text-12);
            color: var(--color-word-4);

            &::after {
                content: '';
                display: block;
                position: absolute;
                left: 0;
                right: 0;
                bottom: calc(-1 * var(--spacing-12));
                height: var(--spacing-12);
                background-color: var(--color-surface-1);
                z-index: 1;
            }

            // Two notices read as two lines of one box, with a hairline
            // between them
            &-item {
                display: flex;
                align-items: flex-start;
                gap: var(--spacing-sm);

                & + & {
                    padding-top: var(--spacing-10);
                    border-top: 1px solid var(--color-surface-3);
                }
            }

            &-icon {
                flex-shrink: 0;
                width: var(--spacing-18);
                height: var(--spacing-18);
            }

            &-text {
                flex: 1;
                min-width: 0;
            }

            // A word of the line's size, on the line: the notice stays one
            // quiet row, not a button bar
            &-action {
                flex-shrink: 0;
                font-size: inherit;
            }
        }

        &__form {
            position: relative;
            display: flex;
            z-index: 1;
            flex-direction: column;

            // The composer is a field too: same border and focus ring as the
            // form fields, on top of the shadow and the 12px radius that make
            // it the container (12) of its 8px buttons
            border-radius: var(--rounded-xl);
            border: 1px solid var(--input-border-color);
            background-color: var(--color-surface);
            padding: var(--spacing-14);
            box-shadow: var(--shadow-lg);
            font-size: var(
                --text-16
            ); // prevent ios zoom on input focus by using a font size >= 16px

            gap: var(--spacing-xs);
            transition: var(--input-transition);

            @include media-breakpoint-up('sm', $breakpoints) {
                font-size: var(--text-14);
                padding: var(--spacing-10);
            }

            &:has(:focus-within) {
                border-color: var(--input-focus-color);
                box-shadow: var(--shadow-lg), var(--input-focus-ring);
            }
        }

        &__fieldset {
            display: flex;
            align-items: center;
            gap: var(--spacing-xs);
            flex: 1;

            // The buttons answer 8px around themselves on a touch screen
            // (PkChatButton): 12px apart, neither takes the other's box
            @media (pointer: coarse) {
                gap: var(--spacing-12);
            }
        }

        &__content {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: var(--spacing-4);
            min-width: 0;
            overflow: hidden;
        }

        &__textarea {
            display: block;
            border: none;
            outline: none;
            resize: none;
            width: 100%;
            max-height: 200px;
            color: var(--color-word-1);
            background-color: var(--color-surface);

            &::placeholder {
                color: var(--color-word-4);
            }
        }

        &__attachments {
            display: flex;
            flex-wrap: wrap;

            // Roomy gap so the uniform-height chips breathe in the row.
            gap: var(--spacing-12);
            padding-bottom: var(--spacing-xs);
            border-bottom: 1px solid var(--color-surface-2);
        }

        // What the user pointed at on the page: one chip, the element's name
        // or the selected text, on one line
        &__pointer {
            display: flex;
            align-items: center;
            gap: var(--spacing-6);
            align-self: flex-start;
            max-width: 100%;
            padding: var(--spacing-4) var(--spacing-4) var(--spacing-4)
                var(--spacing-8);
            border: 1px solid var(--color-surface-3);
            border-radius: var(--rounded-full);
            margin-bottom: var(--spacing-8);
            background-color: var(--color-surface-1);
            color: var(--color-word-2);
            font-size: var(--text-12);
        }

        &__pointer-icon {
            flex-shrink: 0;
            width: var(--spacing-14);
            height: var(--spacing-14);
        }

        &__pointer-label {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &__counter {
            background-color: var(--color-surface-1);
            border: 1px solid var(--color-surface-3);
            border-radius: var(--rounded-md);
            font-size: var(--text-12);
            color: var(--color-word-3);
            line-height: 1;
            padding-inline: var(--spacing-6);
            padding-block: var(--spacing-4);
            z-index: 1;
            font-variant-numeric: tabular-nums;
            font-family: var(--font-mono);
            font-weight: var(--font-bold);

            &--over {
                color: var(--u-danger-text, var(--color-danger));
                border-color: var(--color-danger-lighten-5);
                font-weight: var(--font-bold);
            }
        }
    }
</style>
